// admin/ReportsSection.tsx
import { useMemo, useState, useCallback, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  CheckCircle2,
  Download,
  Play,
  RefreshCw,
  Table as TableIcon,
  X,
  Pencil,
  Save,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ProgressLogger } from "@/components/ProgressLogger";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";
const REPORTS_URL = `${API_BASE}/reports`;

function cleanBearer(t?: string | null) {
  let raw = (t || "").trim();
  if (raw.toLowerCase().startsWith("bearer ")) raw = raw.slice(7).trim();
  return raw;
}

type SubTab = "gestion" | "ajouter";

type AdminReport = {
  id: number;
  name: string;
  source: string;
  active: boolean;
};

type ReportDetail = AdminReport & {
  sql_text?: string | null;
  procedure?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  description?: string | null;
};

export function ReportsSection({
  subTab,
  setSubTab,
}: {
  subTab: SubTab;
  setSubTab: (v: SubTab) => void;
}) {
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-center gap-2">
        <SubTabButton active={subTab === "gestion"} onClick={() => setSubTab("gestion")} label="Gestion des rapports" />
        <SubTabButton active={subTab === "ajouter"} onClick={() => setSubTab("ajouter")} label="Ajouter un nouveau rapport" />
      </div>
      {subTab === "gestion" ? (
        <RapportsGestion refreshKey={refreshKey} onChanged={() => setRefreshKey((k) => k + 1)} />
      ) : (
        <RapportsCreate
          onCreated={() => {
            setRefreshKey((k) => k + 1);
            setSubTab("gestion");
          }}
        />
      )}
    </div>
  );
}

function RapportsGestion({ refreshKey, onChanged }: { refreshKey: number; onChanged: () => void }) {
  const { toast } = useToast();
  const [reports, setReports] = useState<AdminReport[]>([]);
  const [busyCsv, setBusyCsv] = useState<number | null>(null);
  const [busyPreview, setBusyPreview] = useState<number | null>(null);
  const [busyExec, setBusyExec] = useState<number | null>(null);
  const [preview, setPreview] = useState<Record<number, { columns: string[]; rows: any[] }>>({});

  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editSql, setEditSql] = useState("");

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  
  // Format de fichier pour l'export
  const [fileFormat, setFileFormat] = useState<"csv" | "txt" | "xlsx">("csv");

  const authHeaders = useMemo(() => {
    const token = cleanBearer(localStorage.getItem("auth_token"));
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const apiGetReport = useCallback(
    async (id: number): Promise<ReportDetail> => {
      const res = await fetch(`${REPORTS_URL}/${id}`, { headers: { Accept: "application/json", ...authHeaders } });
      if (!res.ok) {
        let d: any = null;
        try {
          d = await res.json();
        } catch {}
        throw new Error(d?.detail || `Rapport introuvable (${res.status})`);
      }
      return res.json();
    },
    [authHeaders]
  );

  const loadReports = useCallback(async () => {
    const res = await fetch(REPORTS_URL, { headers: { Accept: "application/json", ...authHeaders } });
    if (!res.ok) throw new Error(`Chargement des rapports impossible (${res.status})`);
    const data: AdminReport[] = await res.json();
    setReports(data);
  }, [authHeaders]);

  useEffect(() => {
    (async () => {
      try {
        await loadReports();
      } catch (e: any) {
        toast({ title: "Erreur", description: e?.message || "Chargement des rapports échoué", variant: "destructive" });
      }
    })();
  }, [loadReports, refreshKey, toast]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return reports;
    return reports.filter((r) => r.name.toLowerCase().includes(term) || String(r.id).includes(term));
  }, [reports, q]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [q]);

  // ---------- Actions ----------
  const runExecute = useCallback(
    async (r: AdminReport) => {
      try {
        setBusyExec(r.id);
        const res = await fetch(`${REPORTS_URL}/${r.id}/preview?limit=100`, {
          headers: { Accept: "application/json", ...authHeaders },
        });
        const d: any = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(d?.detail || `Exécution impossible (${res.status})`);
        toast({
          title: "✅ Requête exécutée",
          description: `${d?.row_count ?? 0} ligne(s) récupérée(s)${d?.limited ? " (limité)" : ""}`,
        });
      } catch (e: any) {
        toast({ title: "Erreur d'exécution", description: e?.message || "Erreur inconnue", variant: "destructive" });
      } finally {
        setBusyExec(null);
      }
    },
    [authHeaders, toast]
  );

  const runPreview = useCallback(
    async (r: AdminReport) => {
      try {
        setBusyPreview(r.id);
        const res = await fetch(`${REPORTS_URL}/${r.id}/preview?limit=50`, {
          headers: { Accept: "application/json", ...authHeaders },
        });
        const d: any = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(d?.detail || `Prévisualisation impossible (${res.status})`);

        const rows: any[] = Array.isArray(d?.rows) ? d.rows : [];
        const columns: string[] = d?.columns || (rows.length ? Object.keys(rows[0]) : []);
        setPreview((p) => ({ ...p, [r.id]: { columns, rows } }));
      } catch (e: any) {
        toast({ title: "Erreur de prévisualisation", description: e?.message || "Erreur inconnue", variant: "destructive" });
      } finally {
        setBusyPreview(null);
      }
    },
    [authHeaders, toast]
  );

  const closePreview = useCallback((r: AdminReport) => {
    setPreview((p) => {
      const next = { ...p };
      delete next[r.id];
      return next;
    });
  }, []);

  const [downloadProgress, setDownloadProgress] = useState<{
    reportId: number;
    sessionId: string;
  } | null>(null);
  
  // Debug: log quand downloadProgress change
  useEffect(() => {
    console.log(`[ReportsSection] downloadProgress changed:`, downloadProgress);
  }, [downloadProgress]);

  const exportCsv = useCallback(
    async (r: AdminReport) => {
      try {
        // Afficher un toast pour confirmer que le téléchargement démarre
        toast({
          title: "Téléchargement en cours...",
          description: `Génération du fichier ${fileFormat.toUpperCase()} pour ${r.name}`,
          duration: 3000,
        });
        
        setBusyCsv(r.id);
        
        // Générer un session_id unique pour les logs de progression
        // Utiliser une fonction compatible avec tous les navigateurs
        const generateUUID = () => {
          if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
          }
          // Fallback pour les navigateurs qui ne supportent pas crypto.randomUUID
          return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
          });
        };
        const sessionId = generateUUID();
        
        // Ouvrir le dialog AVANT de lancer le téléchargement pour permettre à la connexion SSE de s'établir
        // Forcer un re-render pour que le dialog s'ouvre
        setDownloadProgress({ reportId: r.id, sessionId });
        
        // Attendre un peu pour que le composant React se monte et la connexion SSE se fasse (1 seconde pour être sûr)
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const acceptHeader = fileFormat === "csv" ? "text/csv" : fileFormat === "txt" ? "text/plain" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        console.log(`[exportCsv] Fetching download with sessionId=${sessionId.substring(0, 8)}`);
        const res = await fetch(`${REPORTS_URL}/${r.id}/download?format=${fileFormat}&session_id=${sessionId}`, {
          method: "GET",
          headers: { Accept: acceptHeader, ...authHeaders },
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d?.detail || `Export ${fileFormat.toUpperCase()} impossible (${res.status})`);
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const now = new Date();
        const stamp = now.getFullYear().toString() + 
                     String(now.getMonth() + 1).padStart(2, '0') + 
                     String(now.getDate()).padStart(2, '0') + 
                     String(now.getHours()).padStart(2, '0') + 
                     String(now.getMinutes()).padStart(2, '0') + 
                     String(now.getSeconds()).padStart(2, '0');
        const safe = r.name.replace(/[^\w\s.-]/g, "").replace(/\s+/g, "_");
        a.href = url;
        a.download = `${safe}_${stamp}.${fileFormat}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast({ 
          title: "Téléchargement terminé", 
          description: `Le fichier ${fileFormat.toUpperCase()} a été téléchargé avec succès.`,
        });
      } catch (e: any) {
        toast({ title: `Erreur export ${fileFormat.toUpperCase()}`, description: e?.message || "Erreur inconnue", variant: "destructive" });
      } finally {
        setBusyCsv(null);
        // Garder le dialog ouvert pendant 2 secondes pour voir le message de succès
        setTimeout(() => {
          setDownloadProgress(null);
        }, 2000);
      }
    },
    [authHeaders, toast, fileFormat]
  );

  // ---------- Edit / Delete ----------
  const openEdit = async (r: AdminReport) => {
    try {
      const detail = await apiGetReport(r.id);
      setEditId(r.id);
      setEditName(detail.name);
      setEditSql(detail.sql_text || detail.procedure || "");
    } catch (e: any) {
      toast({ title: "Erreur", description: e?.message || "Ouverture impossible", variant: "destructive" });
    }
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditName("");
    setEditSql("");
  };

  const saveEdit = async (r: AdminReport) => {
    try {
      const body: any = { name: editName.trim() };
      const sqlTrim = (editSql || "").trim();
      if (!body.name) throw new Error("Le nom ne peut pas être vide");
      if (!sqlTrim) throw new Error("La requête SQL / procédure ne peut pas être vide");

      // Heuristique : si commence par EXEC → on envoie procedure ; sinon sql_text
      if (/^\s*exec\b/i.test(sqlTrim)) {
        body.procedure = sqlTrim;
        body.sql_text = undefined;
      } else {
        body.sql_text = sqlTrim;
        body.procedure = undefined;
      }

      const res = await fetch(`${REPORTS_URL}/${r.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json", ...authHeaders },
        body: JSON.stringify(body),
      });
      const d: ReportDetail | { detail?: string } = await res.json().catch(() => ({} as any));
      if (!res.ok) throw new Error((d as any)?.detail || `Mise à jour impossible (${res.status})`);

      setReports((list) =>
        list.map((it) =>
          it.id === r.id
            ? {
                id: (d as ReportDetail).id,
                name: (d as ReportDetail).name,
                source: (d as ReportDetail).source,
                active: (d as ReportDetail).active,
              }
            : it
        )
      );
      toast({ title: "✅ Rapport modifié" });
      cancelEdit();
      onChanged();
    } catch (e: any) {
      toast({ title: "Erreur", description: e?.message || "Mise à jour échouée", variant: "destructive" });
    }
  };

  const deleteReport = async (r: AdminReport) => {
    const ok = window.confirm(`Supprimer définitivement « ${r.name} » ?`);
    if (!ok) return;
    try {
      const res = await fetch(`${REPORTS_URL}/${r.id}`, {
        method: "DELETE",
        headers: { Accept: "application/json", ...authHeaders },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.detail || `Suppression impossible (${res.status})`);
      }
      setReports((list) => list.filter((it) => it.id !== r.id));
      setPreview((p) => {
        const next = { ...p };
        delete next[r.id];
        return next;
      });
      toast({ title: "🗑️ Rapport supprimé" });
      if (editId === r.id) cancelEdit();
    } catch (e: any) {
      toast({ title: "Erreur", description: e?.message || "Suppression échouée", variant: "destructive" });
    }
  };

  return (
    <Card className="p-8 bg-gradient-card backdrop-blur-sm border-glass">
      <div className="flex items-center justify-between gap-3 mb-6">
        <h2 className="text-2xl font-semibold">Gestion des Rapports</h2>
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-2 mr-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par nom ou ID…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-[240px]"
            />
          </div>
          <Select value={fileFormat} onValueChange={(v) => setFileFormat(v as "csv" | "txt" | "xlsx")}>
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="csv">CSV</SelectItem>
              <SelectItem value="txt">TXT</SelectItem>
              <SelectItem value="xlsx">Excel</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={async () => {
              try {
                await loadReports();
                toast({ title: "Liste actualisée" });
              } catch (e: any) {
                toast({ title: "Erreur", description: e?.message || "Actualisation échouée", variant: "destructive" });
              }
            }}
            title="Rafraîchir la liste"
          >
            <RefreshCw className="h-4 w-4" />
            Reload
          </Button>
        </div>
      </div>

      <div className="sm:hidden mb-4">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par nom ou ID…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <div className="flex items-center justify-between mb-4 text-sm text-muted-foreground">
        <div>
          {total} rapport{total > 1 ? "s" : ""} • page {currentPage} / {totalPages}
          {q.trim() && <span className="ml-2">• filtre: “{q.trim()}”</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Précédent
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="gap-1"
          >
            Suivant
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-8">
        {pageItems.length > 0 ? (
          pageItems.map((r) => {
            const p = preview[r.id];
            const isEditing = editId === r.id;

            return (
              <div key={r.id} className="border-b last:border-b-0 pb-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">{r.name}</h3>

                  <div className="flex items-center gap-2 sm:gap-3">
                    {r.active ? (
                      <Badge variant="secondary" className="gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                        Actif
                      </Badge>
                    ) : (
                      <Badge variant="outline">Inactif</Badge>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => runExecute(r)}
                      disabled={busyExec === r.id}
                      title="Exécuter (via /reports/{id}/preview)"
                    >
                      <Play className="h-4 w-4" />
                      Exécuter
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => runPreview(r)}
                      disabled={busyPreview === r.id}
                      title="Prévisualiser"
                    >
                      <TableIcon className="h-4 w-4" />
                      Prévisualiser
                    </Button>

                    <Button
                      variant="default"
                      size="sm"
                      className="gap-2"
                      onClick={() => exportCsv(r)}
                      disabled={busyCsv === r.id}
                      title={`Exporter ${fileFormat.toUpperCase()}`}
                    >
                      <Download className="h-4 w-4" />
                      Export {fileFormat.toUpperCase()}
                    </Button>

                    {!isEditing ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => openEdit(r)}
                        title="Modifier"
                      >
                        <Pencil className="h-4 w-4" />
                        Modifier
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          onClick={() => saveEdit(r)}
                          title="Enregistrer"
                          disabled={!editName.trim() || !editSql.trim()}
                        >
                          <Save className="h-4 w-4" />
                          Enregistrer
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-2"
                          onClick={cancelEdit}
                          title="Annuler"
                        >
                          <X className="h-4 w-4" />
                          Annuler
                        </Button>
                      </>
                    )}

                    <Button
                      variant="destructive"
                      size="sm"
                      className="gap-2"
                      onClick={() => deleteReport(r)}
                      title="Supprimer le rapport"
                    >
                      <Trash2 className="h-4 w-4" />
                      Supprimer
                    </Button>
                  </div>
                </div>

                {isEditing && (
                  <div className="mt-4 rounded-md border p-4 bg-muted/30">
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label>Nom du rapport</Label>
                        <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
                      </div>

                      <div className="space-y-2">
                        <Label>Requête SQL / Procédure</Label>
                        <textarea
                          className="w-full min-h-[130px] border rounded-md p-2 text-sm font-mono"
                          value={editSql}
                          onChange={(e) => setEditSql(e.target.value)}
                          placeholder="SELECT …  ou  EXEC dbo.MaProc @p=1"
                        />
                        <p className="text-xs text-muted-foreground">
                          Pour une procédure, commence par <code>EXEC …</code>.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  {p && p.columns.length ? (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs text-muted-foreground">
                          Prévisualisation • {p.rows.length} lignes • {p.columns.length} colonnes (max 50)
                        </div>
                        <Button variant="ghost" size="sm" className="h-7 px-2 gap-1" onClick={() => closePreview(r)} title="Fermer">
                          <X className="h-4 w-4" />
                          Fermer
                        </Button>
                      </div>
                      <div className="overflow-auto border rounded-md">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50">
                            <tr>
                              {p.columns.map((c) => (
                                <th key={c} className="text-left px-3 py-2 whitespace-nowrap font-medium">
                                  {c}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {p.rows.map((row, i) => (
                              <tr key={i} className="border-t">
                                {p.columns.map((c) => (
                                  <td key={c} className="px-3 py-2 whitespace-nowrap">
                                    {formatCell(row[c])}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <Card className="p-4 border-dashed">
                      <p className="text-sm text-muted-foreground">
                        Clique sur <b>Prévisualiser</b> pour afficher les données.
                      </p>
                    </Card>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <Card className="p-6 border-dashed">
            <p className="text-sm text-muted-foreground">
              {q.trim() ? "Aucun rapport ne correspond à votre recherche." : "Aucun rapport trouvé."}
            </p>
          </Card>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between text-sm">
        <div className="text-muted-foreground">
          {total} rapport{total > 1 ? "s" : ""} • page {currentPage} / {totalPages}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Précédent
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="gap-1"
          >
            Suivant
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Dialog de progression pour les téléchargements */}
      <Dialog open={downloadProgress !== null} onOpenChange={(open) => {
        if (!open) setDownloadProgress(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Progression du téléchargement</DialogTitle>
            <DialogDescription>
              Suivi en temps réel de la génération et du téléchargement du fichier
            </DialogDescription>
          </DialogHeader>
          {downloadProgress ? (
            <>
              {console.log(`[ReportsSection] Rendering ProgressLogger with sessionId=${downloadProgress.sessionId?.substring(0, 8)}...`)}
              <ProgressLogger
                sessionId={downloadProgress.sessionId}
                reportId={downloadProgress.reportId}
                onComplete={() => {
                  console.log(`[ReportsSection] ProgressLogger onComplete called`);
                  // Le dialog se fermera automatiquement après 2 secondes dans exportCsv
                }}
                onError={(error) => {
                  console.error(`[ReportsSection] ProgressLogger onError:`, error);
                  toast({
                    title: "Erreur lors du téléchargement",
                    description: error,
                    variant: "destructive",
                  });
                }}
              />
            </>
          ) : (
            <div className="p-4 text-center text-muted-foreground">
              <p>Pas de session de téléchargement active</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function RapportsCreate({ onCreated }: { onCreated: () => void }) {
  const { toast } = useToast();
  const [state, setState] = useState({ name: "", procedure: "", sqlText: "", description: "" });

  const authHeaders = useMemo(() => {
    const token = cleanBearer(localStorage.getItem("auth_token"));
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const onChange =
    (key: keyof typeof state) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setState((s) => ({ ...s, [key]: e.target.value }));

  const exactlyOne = (state.procedure.trim() ? 1 : 0) + (state.sqlText.trim() ? 1 : 0) === 1;

  const disabled = !state.name.trim() || !exactlyOne;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: any = {
        name: state.name.trim(),
        description: state.description.trim() || undefined,
        active: true,
      };
      if (state.procedure.trim()) {
        payload.procedure = state.procedure.trim().startsWith("EXEC")
          ? state.procedure.trim()
          : `EXEC ${state.procedure.trim()}`;
      } else {
        payload.sql_text = state.sqlText.trim();
      }
      const res = await fetch(REPORTS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", ...authHeaders },
        body: JSON.stringify(payload),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.detail || `Création impossible (${res.status})`);
      toast({ title: "✅ Rapport créé" });
      setState({ name: "", procedure: "", sqlText: "", description: "" });
      onCreated();
    } catch (e: any) {
      toast({ title: "Erreur", description: e?.message || "Création échouée", variant: "destructive" });
    }
  };

  return (
    <Card className="p-8 bg-gradient-card backdrop-blur-sm border-glass">
      <h2 className="text-2xl font-semibold mb-6">Ajouter un nouveau rapport</h2>

      <form onSubmit={onSubmit} className="grid gap-6 sm:grid-cols-2">
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="name">Nom du rapport</Label>
          <Input id="name" placeholder="Ex: Commandes Tous Canaux" value={state.name} onChange={onChange("name")} />
        </div>

        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="procedure">Procédure stockée</Label>
          <Input id="procedure" placeholder="Ex: dbo.PS_FD_SO_ALL_CHN_M1" value={state.procedure} onChange={onChange("procedure")} />
        </div>

        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="sql">Requête SQL</Label>
          <textarea
            id="sql"
            className="w-full min-h-[110px] border rounded-md p-2 text-sm font-mono"
            placeholder="Ex: SELECT TOP 100 * FROM dbo.Orders WHERE Status='Open'"
            value={state.sqlText}
            onChange={onChange("sqlText")}
          />
          <p className="text-xs text-muted-foreground">
            Renseignez <b>soit</b> la procédure <b>soit</b> la requête SQL (exactement un seul des deux).
          </p>
          {!exactlyOne && (
            <p className="text-xs text-red-600">
              Veuillez remplir exactement un des deux champs (Procédure OU Requête SQL).
            </p>
          )}
        </div>

        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="desc">Description (optionnel)</Label>
          <Input id="desc" placeholder="Brève description du rapport" value={state.description} onChange={onChange("description")} />
        </div>

        <div className="sm:col-span-2 flex items-center gap-2">
          <Button type="submit" disabled={disabled}>
            Enregistrer
          </Button>
        </div>
      </form>
    </Card>
  );
}

function formatCell(v: any) {
  if (v === null || v === undefined) return "";
  if (v instanceof Date) return v.toISOString().slice(0, 19).replace("T", " ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function SubTabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "rounded-full px-4 py-2 text-sm border transition",
        active ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/80"
      )}
    >
      {label}
    </button>
  );
}
